import classNames from 'classnames'
import Spinner from 'app/components/Spinner'

export type ButtonProps = {
  className?: string
  label?: string
  size?: `xs` | `sm` | `md` | `lg` | `xl`
  mode: `primary` | `secondary` | `white` | `tertiary` | `danger`
  type?: `button` | `submit` | `reset`
  disabled?: boolean
  onClick?: (evt?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  children?: React.ReactNode
  loading?: boolean
  form?: string
  id?: string
}
/**
/**
/**
 * Button component
 *
 * A consistent button component supporting multiple sizes, modes, loading state, and custom children.
 *
 * ### Size Classes
 * - `size` prop maps to CSS classes which set padding and font size:
 *   - `"xs"` or `"sm"` → `btn-sm` — 28px height, 12px font-size
 *   - `"md"` (default) → `btn-md` — 36px height, 14px font-size
 *   - `"lg"` or `"xl"` → `btn-lg` — 44px height, 16px font-size
 *
 * ### Mode/Color Classes
 * - `mode` prop selects button color:
 *   - `"primary"`: **Blue** background (`#155EEF`), white text
 *   - `"secondary"`: **Gray** background (`#EAECF0`), gray text (`#475467`)
 *   - `"white"`: **White** background (`#fff`), gray border (`#D0D5DD`), neutral text (`#344054`)
 *   - `"tertiary"`: **Transparent** background, blue/primary colored border & text (`#155EEF`)
 *   - `"danger"`: **Red** background (`#F04438`), white text
 *
 * ### Props
 * - `className?`: Custom class for the button.
 * - `label?`: The text to render within the button.
 * - `size?`: One of `"xs"` | `"sm"` | `"md"` | `"lg"` | `"xl"` (default: `"md"`).
 * - `mode`: Visual style, one of `"primary"` | `"secondary"` | `"white"` | `"tertiary"` | `"danger"`.
 * - `type?`: Standard button type, `"button"` | `"submit"` | `"reset"`. Defaults to `"button"`.
 * - `disabled?`: Disables the button if true.
 * - `onClick?`: onClick event handler.
 * - `children?`: Custom children to render inside the button.
 * - `loading?`: Shows a Spinner and disables the button if true.
 * - `form?`: The form attribute value for the button (used for referencing a form by ID).
 * - `id?`: HTML id attribute for the button.
 *
 * ### Examples
 * ```jsx
 * // Primary, Medium (default) - blue 36px button
 * <Button mode="primary" label="Save" />
 *
 * // Secondary, Small - gray 28px button
 * <Button mode="secondary" size="sm" label="Edit" />
 *
 * // Danger, Large, loading - red 44px button, spinner shown
 * <Button mode="danger" size="lg" label="Delete" loading />
 *
 * // White, Extra Small, with icon child
 * <Button mode="white" size="xs">
 *   <Icon />
 *   "Archive"
 * </Button>
 *
 * // Tertiary with custom onClick - transparent blue text/border
 * <Button mode="tertiary" onClick={() => alert('Clicked!')} label="Help" />
 * ```
 *
 * The button supports both `label` and custom `children`. If `loading` is true, a Spinner will be shown and the button is disabled.
 */
export default function Button({
  className,
  label,
  size = `md`,
  mode,
  form,
  type = `button`,
  disabled = false,
  loading = false,
  onClick,
  children,
  id,
}: ButtonProps) {
  const btnClass = classNames({
    btn: true,
    'btn-tertiary': mode === `tertiary` || mode === `white`,
    'btn-sm': size === `sm` || size === `xs`,
    'btn-md': size === `md`,
    'btn-lg': size === `lg` || size === `xl`,
    'btn-primary': mode === `primary`,
    'btn-secondary': mode === `secondary`,
    'btn-danger': mode === `danger`,
    [className || ``]: className != null,
  })

  return (
    <>
      <button type={type} form={form} className={btnClass} onClick={onClick} disabled={disabled || loading} id={id}>
        {loading ? <Spinner /> : label}
        {children}
      </button>
    </>
  )
}
